// src/components/switchClients/SwitchClientList.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  useTheme,
  TablePagination,
  useMediaQuery,
  Avatar,
  Select,
  MenuItem,
  InputLabel,
  Collapse,
  Button,
  Badge,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search,
  ElectricBolt,
  Person,
  Business,
  FilterList,
  FilterListOff,
  Clear,
  LocalFireDepartment,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import { CLIENT_TAGS, CLIENT_TYPES } from "../../utils/models/clientModel";
import {
  SWITCH_STATUS,
  SWITCH_STATUS_LABELS,
  SWITCH_STATUS_COLORS,
} from "../../utils/models/switchClientModel";

const SwitchClientList = ({ onRefresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const { data: clients, loading } = useCollection("clients");
  const { update } = useAuditFirestore("clients");
  
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filtrar solo clientes con el tag CAMBIO_COMERCIALIZADORA
  const switchClients = clients?.filter(
    (client) => 
      client.isActive !== false && 
      client.tags?.includes(CLIENT_TAGS.CAMBIO_COMERCIALIZADORA)
  ) || [];

  // Aplicar todos los filtros
  const filtered = switchClients.filter((c) => {
    // Filtro por búsqueda
    const matchesSearch = search === "" ||
      `${c.firstName || ""} ${c.lastName || ""} ${c.companyName || ""}`
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.phone?.toLowerCase().includes(search.toLowerCase()) ||
      (c.cups || c.addresses?.[0]?.cups || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (c.cupsGas || c.addresses?.[0]?.cupsGas || "")
        .toLowerCase()
        .includes(search.toLowerCase());

    // Filtro por estado del switch
    const currentStatus = c.switchStatus || SWITCH_STATUS.PENDING;
    const matchesStatus = statusFilter === "all" || currentStatus === statusFilter;

    // Filtro por tipo de cliente
    const matchesType = typeFilter === "all" || c.type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Paginación
  const paginatedClients = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = async (clientId, newStatus) => {
    try {
      await update(clientId, {
        switchStatus: newStatus,
        updatedAt: new Date().toISOString(),
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error updating switch status:", error);
    }
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
  };

  const activeFiltersCount = [
    search !== "",
    statusFilter !== "all",
    typeFilter !== "all",
  ].filter(Boolean).length;

  const getRowStyle = (status) => {
    const color = SWITCH_STATUS_COLORS[status] || SWITCH_STATUS_COLORS.pending;
    return {
      borderLeft: `4px solid ${color}`,
      backgroundColor: `${color}10`,
    };
  };

  const getDisplayName = (client) => {
    if (client.type === "company") {
      return client.companyName || "Sin nombre";
    }
    return (
      `${client.firstName || ""} ${client.lastName || ""}`.trim() ||
      "Sin nombre"
    );
  };

  const getCupsLuz = (client) => {
    return client.cups || client.addresses?.[0]?.cups || null;
  };

  const getCupsGas = (client) => {
    return client.cupsGas || client.addresses?.[0]?.cupsGas || null;
  };

  // Contar por estado
  const countByStatus = (status) => {
    return switchClients.filter((c) => (c.switchStatus || SWITCH_STATUS.PENDING) === status).length;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      {/* Barra de búsqueda y filtros */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0, 95, 101, 0.12)",
          background: theme.palette.background.paper,
        }}
      >
        <Stack spacing={2}>
          {/* Barra principal */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={1.5}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flex={1}>
              {/* SEARCH */}
              <TextField
                placeholder="Buscar por nombre, email, CUPS..."
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{
                  width: { xs: "100%", sm: 280, md: 360 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: theme.palette.background.paper,
                    height: 44,
                    "& fieldset": { borderColor: theme.palette.divider },
                    "&:hover fieldset": { borderColor: theme.palette.primary.main },
                    "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>✖</IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* FILTRO ESTADO */}
              <FormControl size="small" sx={{ width: { xs: "100%", sm: 160 }, height: 44 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  label="Estado"
                  sx={{ borderRadius: "12px", "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider } }}
                >
                  <MenuItem value="all">Todos ({switchClients.length})</MenuItem>
                  {Object.entries(SWITCH_STATUS_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: SWITCH_STATUS_COLORS[value] }} />
                          {label}
                        </Box>
                        <Chip label={countByStatus(value)} size="small" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }} />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {/* BOTÓN FILTROS */}
              <Badge badgeContent={activeFiltersCount} color="primary" invisible={activeFiltersCount === 0}>
                <Button
                  variant={showFilters || activeFiltersCount > 0 ? "contained" : "outlined"}
                  size="small"
                  startIcon={showFilters ? <FilterListOff /> : <FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    height: 44,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: theme.palette.primary.main,
                    color: showFilters || activeFiltersCount > 0 ? "white" : theme.palette.primary.main,
                    backgroundColor: showFilters || activeFiltersCount > 0 ? theme.palette.primary.main : "transparent",
                    "&:hover": {
                      backgroundColor: showFilters ? theme.palette.primary.dark : `${theme.palette.primary.main}10`,
                    },
                  }}
                >
                  Filtros
                </Button>
              </Badge>

              {/* CONTADOR */}
              <Chip
                icon={<ElectricBolt />}
                label={`${filtered.length} clientes`}
                sx={{
                  height: 44,
                  borderRadius: "12px",
                  fontWeight: 600,
                  background: `${theme.palette.primary.main}15`,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}40`,
                  "& .MuiChip-icon": { color: theme.palette.primary.main },
                }}
              />
            </Stack>
          </Stack>

          {/* FILTROS AVANZADOS */}
          <Collapse in={showFilters}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                {/* FILTRO TIPO */}
                <FormControl size="small" sx={{ width: { xs: "100%", sm: 180 } }}>
                  <InputLabel>Tipo de cliente</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
                    label="Tipo de cliente"
                    sx={{ borderRadius: "12px" }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value={CLIENT_TYPES.INDIVIDUAL}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Person sx={{ fontSize: 16 }} />
                        Particular
                      </Box>
                    </MenuItem>
                    <MenuItem value={CLIENT_TYPES.COMPANY}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Business sx={{ fontSize: 16 }} />
                        Empresa
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* BOTÓN LIMPIAR */}
                {activeFiltersCount > 0 && (
                  <Button
                    size="small"
                    startIcon={<Clear />}
                    onClick={clearAllFilters}
                    sx={{
                      height: 40,
                      borderRadius: "12px",
                      textTransform: "none",
                      color: theme.palette.error.main,
                      "&:hover": { backgroundColor: `${theme.palette.error.main}10` },
                    }}
                  >
                    Limpiar filtros
                  </Button>
                )}
              </Stack>

              {/* CHIPS DE FILTROS ACTIVOS */}
              {activeFiltersCount > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {statusFilter !== "all" && (
                    <Chip
                      size="small"
                      icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: SWITCH_STATUS_COLORS[statusFilter], ml: 1 }} />}
                      label={SWITCH_STATUS_LABELS[statusFilter]}
                      onDelete={() => setStatusFilter("all")}
                      sx={{ borderRadius: "8px", backgroundColor: `${SWITCH_STATUS_COLORS[statusFilter]}20`, color: SWITCH_STATUS_COLORS[statusFilter], fontWeight: 600 }}
                    />
                  )}
                  {typeFilter !== "all" && (
                    <Chip
                      size="small"
                      icon={typeFilter === CLIENT_TYPES.INDIVIDUAL ? <Person sx={{ fontSize: 14 }} /> : <Business sx={{ fontSize: 14 }} />}
                      label={typeFilter === CLIENT_TYPES.INDIVIDUAL ? "Particular" : "Empresa"}
                      onDelete={() => setTypeFilter("all")}
                      sx={{ borderRadius: "8px", backgroundColor: `${theme.palette.primary.main}20`, color: theme.palette.primary.main, fontWeight: 600 }}
                    />
                  )}
                  {search && (
                    <Chip
                      size="small"
                      label={`Búsqueda: "${search}"`}
                      onDelete={() => setSearch("")}
                      sx={{ borderRadius: "8px", backgroundColor: `${theme.palette.info.main}20`, color: theme.palette.info.main, fontWeight: 600 }}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      {filtered.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <ElectricBolt sx={{ fontSize: 64, color: theme.palette.primary.main, opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
            {activeFiltersCount > 0 ? "Sin resultados" : "Sin clientes para switch"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: activeFiltersCount > 0 ? 2 : 0 }}>
            {activeFiltersCount > 0
              ? "Prueba a cambiar los filtros"
              : 'No hay clientes con la etiqueta "Cambio de comercializadora"'}
          </Typography>
          {activeFiltersCount > 0 && (
            <Button variant="outlined" size="small" onClick={clearAllFilters} sx={{ borderRadius: "12px", textTransform: "none", mt: 1 }}>
              Limpiar filtros
            </Button>
          )}
        </Box>
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 95, 101, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, width: 10, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }} />
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                    Cliente
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}>Teléfono</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          CUPS Luz
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          CUPS Gas
                        </Box>
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                    Estado
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedClients.map((client) => {
                  const currentStatus = client.switchStatus || SWITCH_STATUS.PENDING;
                  const cupsLuz = getCupsLuz(client);
                  const cupsGas = getCupsGas(client);
                  
                  return (
                    <TableRow
                      key={client.id}
                      hover
                      sx={{
                        ...getRowStyle(currentStatus),
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : `${SWITCH_STATUS_COLORS[currentStatus]}15`,
                        },
                      }}
                    >
                      <TableCell>
                        <Avatar
                          sx={{
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            background: SWITCH_STATUS_COLORS[currentStatus],
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                        </Avatar>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                          {getDisplayName(client)}
                        </Typography>
                        {isMobile && (
                          <Stack spacing={0.3} sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", display: "block" }}>
                              {client.email || client.phone || "Sin contacto"}
                            </Typography>
                            {cupsLuz && (
                              <Chip
                                label={cupsLuz}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: "0.65rem",
                                  height: 20,
                                  borderColor: theme.palette.warning.main,
                                  color: theme.palette.warning.main,
                                  "& .MuiChip-icon": { color: theme.palette.warning.main },
                                }}
                              />
                            )}
                            {cupsGas && (
                              <Chip
                                label={cupsGas}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: "0.65rem",
                                  height: 20,
                                  borderColor: theme.palette.error.main,
                                  color: theme.palette.error.main,
                                  "& .MuiChip-icon": { color: theme.palette.error.main },
                                }}
                              />
                            )}
                          </Stack>
                        )}
                      </TableCell>

                      {!isMobile && (
                        <>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>{client.email || "—"}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>{client.phone || "—"}</Typography>
                          </TableCell>
                          <TableCell>
                            {cupsLuz ? (
                              <Tooltip title="CUPS Luz">
                                <Chip
                                  label={cupsLuz}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: "0.75rem",
                                    borderColor: theme.palette.warning.main,
                                    color: theme.palette.warning.main,
                                    fontWeight: 500,
                                    "& .MuiChip-icon": { color: theme.palette.warning.main },
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "text.secondary" }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {cupsGas ? (
                              <Tooltip title="CUPS Gas">
                                <Chip
                                  label={cupsGas}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: "0.75rem",
                                    borderColor: theme.palette.error.main,
                                    color: theme.palette.error.main,
                                    fontWeight: 500,
                                    "& .MuiChip-icon": { color: theme.palette.error.main },
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "text.secondary" }}>—</Typography>
                            )}
                          </TableCell>
                        </>
                      )}

                      <TableCell>
                        <FormControl component="fieldset">
                          <RadioGroup
                            row
                            value={currentStatus}
                            onChange={(e) => handleStatusChange(client.id, e.target.value)}
                            sx={{ gap: { xs: 0.5, sm: 1 } }}
                          >
                            {Object.entries(SWITCH_STATUS_LABELS).map(([value, label]) => (
                              <FormControlLabel
                                key={value}
                                value={value}
                                control={
                                  <Radio
                                    size="small"
                                    sx={{
                                      color: SWITCH_STATUS_COLORS[value],
                                      "&.Mui-checked": { color: SWITCH_STATUS_COLORS[value] },
                                      padding: { xs: "4px", sm: "6px" },
                                    }}
                                  />
                                }
                                label={
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                      fontWeight: currentStatus === value ? 600 : 400,
                                      color: SWITCH_STATUS_COLORS[value],
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                }
                                sx={{ mr: 0 }}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": { minHeight: 48, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.75rem", sm: "0.85rem" } },
            }}
          />
        </Card>
      )}
    </Box>
  );
};

export default SwitchClientList;